import { useEffect, useRef } from 'react';
import { useOrganizationList } from '@clerk/clerk-react';

const getLoadedMembershipPageKey = (userMemberships) => {
  const membershipIds = (userMemberships?.data || []).map((membership) => membership.id || '');
  return `${userMemberships?.count ?? 'unknown'}:${membershipIds.join(',')}`;
};

/**
 * Clerk returns organization memberships ten at a time by default. `infinite`
 * appends subsequent pages, but it does not request them automatically, so keep
 * advancing until the current user's complete membership list is loaded.
 */
export function useCompleteOrganizationList() {
  const organizationList = useOrganizationList({
    userMemberships: {
      infinite: true,
    },
  });
  const { isLoaded, userMemberships } = organizationList;
  const lastRequestedPageRef = useRef(null);

  const hasNextPage = Boolean(userMemberships?.hasNextPage);
  const isFetching = Boolean(userMemberships?.isFetching);
  const fetchNext = userMemberships?.fetchNext;
  const loadedMembershipPageKey = getLoadedMembershipPageKey(userMemberships);

  useEffect(() => {
    if (!isLoaded || !hasNextPage || isFetching || typeof fetchNext !== 'function') return;

    // React Strict Mode may replay effects. Key the request to the memberships
    // already loaded so the same next page is never requested twice.
    if (lastRequestedPageRef.current === loadedMembershipPageKey) return;
    lastRequestedPageRef.current = loadedMembershipPageKey;

    try {
      void Promise.resolve(fetchNext()).catch((error) => {
        // Keep the memberships already loaded and stop at this page. A reload
        // creates a fresh hook instance and safely retries the Clerk request.
        console.warn('[ClerkAuth] failed to load another organization membership page', error);
      });
    } catch (error) {
      console.warn('[ClerkAuth] failed to load another organization membership page', error);
    }
  }, [fetchNext, hasNextPage, isFetching, isLoaded, loadedMembershipPageKey]);

  return organizationList;
}
